import React from 'react'
import PropTypes from 'prop-types'
import { castArray, debounce, isEmpty, isEqual, isNil, split, uniqWith } from 'lodash'
import SelectField from './SelectField'
import Component from './Component'

export default class RemoteOptionsSelectField extends Component {
  static propTypes = {
    handleChange: PropTypes.func.isRequired,
    handleBlur: PropTypes.func,
    filterOption: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    name: PropTypes.string.isRequired,
    errorMessage: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.array
    ]),
    disabled: PropTypes.bool,
    placeholder: PropTypes.string,
    label: PropTypes.string,
    clearable: PropTypes.bool,

    recordsRequest: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    requestInProgress: PropTypes.bool,
    records: PropTypes.array,
    recordLabel: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.string
    ]),
    recordValue: PropTypes.string,
    searchType: PropTypes.string,
    autoFocus: PropTypes.bool,
    multi: PropTypes.bool,
    filterFields: PropTypes.object,
    searchColumns: PropTypes.array
  }

  static defaultProps = {
    requestInProgress: false,
    recordLabel: ['name'],
    recordValue: 'id',
    searchType: 'search_term'
  }

  recordsRequest = debounce((value) => {
    // Gate on the trimmed length so the 2-character minimum measures real characters --
    // otherwise "  a" clears the bar and fires a request that can never match. The trimmed
    // value is also what gets stored below, so `noResultsText` reports the same threshold the
    // gate applied rather than counting padding as characters typed.
    const searchValue = (value ?? '').trim()

    if (searchValue.length >= 2) {
      const recordRequestQuery = {}
      recordRequestQuery[this.props.searchType] = searchValue

      if (this.props.filterFields) {
        Object.keys(this.props.filterFields).forEach((key) => {
          recordRequestQuery[key] = this.props.filterFields[key]
        })
      }

      this.props.recordsRequest(recordRequestQuery).then((res) => {
        this.setState({ records: res.records })
      })
    } else if (!isEmpty(this.props.records)) {
      this.props.reset()
      this.setState({ records: [] })
    }

    this.setState({ value: searchValue })
  }, 250)

  constructor(props) {
    super(props)

    this.state = {
      value: '',
      records: props.records
    }
  }

  componentWillUnmount() {
    this.recordsRequest.cancel()
  }

  get options() {
    if (this.props.requestInProgress) {
      return this.selectedOptions
    }

    return uniqWith([
      ...this.selectedOptions,
      ...this.searchOptions
    ], isEqual)
  }

  get searchOptions() {
    const { records } = this.state

    if (isNil(records)) return []

    return this.transformOptions(records)
  }

  get selectedOptions() {
    const { selectedOptions } = this.state

    if (isNil(selectedOptions)) return this.transformOptions(this.props.records || [])

    return castArray(selectedOptions).map((o) => ({ text: o.label, value: o.value }))
  }

  get noResultsText() {
    if (this.props.requestInProgress) {
      return false
    }

    if (this.state.value.length < 2) {
      return 'Enter at least 2 characters to search'
    }
    return 'No results'
  }

  transformOptions = (values) => {
    return values.map((group) => ({
      text: this.labelText(group),
      value: group[this.props.recordValue]
    }))
  }

  labelText = (group) => {
    if (!Array.isArray(this.props.recordLabel)) {
      return group[this.props.recordLabel]
    }

    return this.props.recordLabel.map((recordLabel) => {
      if (recordLabel.includes('.')) {
        const keys = split(recordLabel, '.', 2)
        return group[keys[0]][keys[1]]
      }

      return group[recordLabel]
    }).join(' - ')
  }

  handleChange = (name, value, options) => {
    let returnValue

    if (this.props.multi && isNil(value)) {
      returnValue = []
    } else {
      returnValue = value
    }

    this.setState({ selectedOptions: options }, () => {
      this.props.handleChange(name, returnValue, options)
    })
  }

  handleInputChange = (value) => {
    // Compare on the same normalized basis that the request and the stored value use --
    // otherwise typing a trailing space re-fires a request for a term already searched.
    if ((value ?? '').trim() !== this.state.value) {
      this.recordsRequest(value)
    }
  }

  render() {
    return (
      <SelectField
        name={this.props.name}
        label={this.props.label}
        handleChange={this.handleChange}
        handleInputChange={this.handleInputChange}
        errorMessage={this.props.errorMessage}
        value={this.props.value}
        handleBlur={this.props.handleBlur}
        disabled={this.props.disabled}
        placeholder={this.props.placeholder}
        clearable={this.props.clearable}
        options={this.options}
        isLoading={this.props.requestInProgress}
        noResultsText={this.noResultsText}
        autoFocus={this.props.autoFocus}
        multi={this.props.multi}
        filterOption={this.props.filterOption}
        searchColumns={this.props.searchColumns}
      />
    )
  }
}
